import { Modifier } from '@shared/modifiers/ModifierSystem';
import { TriggerEffect } from '@shared/modifiers/StatCalculator';

// ========================
// Types
// ========================

export type PassiveNodeType = 'small' | 'notable' | 'keystone' | 'branch_start' | 'center'
  // Legacy types kept for UI compat until Faz 6 rewrite
  | 'start' | 'mastery' | 'jewel_socket' | 'gateway';

export type NodeCategory =
  | 'strength' | 'agility' | 'spirit'
  | 'life' | 'mana' | 'defense'
  | 'physical' | 'attack_speed' | 'crit'
  | 'fire' | 'lightning' | 'cold' | 'earth' | 'holy'
  | 'evasion' | 'speed' | 'leech'
  | 'regen' | 'dot' | 'minion'
  | 'energy_shield' | 'spell'
  // Legacy categories kept for UI compat until Faz 6 rewrite
  | 'mastery' | 'jewel';

export const CATEGORY_ICONS: Record<NodeCategory, string> = {
  strength: '💪',
  agility: '🦅',
  spirit: '🔮',
  life: '❤️',
  mana: '💧',
  defense: '🛡️',
  physical: '⚔️',
  attack_speed: '🗡️',
  crit: '🎯',
  fire: '🔥',
  lightning: '⚡',
  cold: '❄️',
  earth: '🌍',
  holy: '✨',
  evasion: '💨',
  speed: '🏃',
  leech: '💀',
  regen: '💚',
  dot: '☠️',
  minion: '👻',
  energy_shield: '🔷',
  spell: '🌀',
  // Legacy
  mastery: '🌟',
  jewel: '💎',
};

// ========================
// PassiveBonuses
// ========================

export interface PassiveBonuses {
  flatHp?: number;
  flatMana?: number;
  flatArmor?: number;
  flatEvasion?: number;
  flatStrength?: number;
  flatAgility?: number;
  flatSpirit?: number;
  percentHp?: number;
  percentMana?: number;
  percentDamage?: number;
  percentPhysicalDamage?: number;
  percentElementalDamage?: number;
  percentFireDamage?: number;
  percentLightningDamage?: number;
  percentColdDamage?: number;
  percentDarkDamage?: number;
  percentSpellDamage?: number;
  percentAttackSpeed?: number;
  percentCastSpeed?: number;
  percentMovementSpeed?: number;
  percentCritChance?: number;
  percentCritDamage?: number;
  percentArmor?: number;
  percentEvasion?: number;
  percentEnergyShield?: number;
  percentManaRegen?: number;
  percentHpRegen?: number;
  percentCooldownReduction?: number;
  extraDodgeCharges?: number;
  lifeLeechPercent?: number;
  manaLeechPercent?: number;
  projectileCount?: number;
  areaOfEffect?: number;
  dotDamage?: number;
  minionDamage?: number;
  minionLife?: number;
  lifeOnKill?: number;
  manaOnKill?: number;
  maxTotem?: number;
  bloodMagic?: boolean;
  mindOverMatter?: boolean;
}

// ========================
// Branch & Node types
// ========================

export type BranchId = 'demir' | 'kale' | 'avci' | 'golge' | 'firtina' | 'ruhlar';

export const BRANCH_NAMES: Record<BranchId, string> = {
  demir: 'Demir',
  kale: 'Kale',
  avci: 'Avcı',
  golge: 'Gölge',
  firtina: 'Fırtına',
  ruhlar: 'Ruhlar',
};

export const BRANCH_DESCRIPTIONS: Record<BranchId, string> = {
  demir: 'Yakın dövüş hasarı, fiziksel güç',
  kale: 'Tank, zırh, can',
  avci: 'Menzilli, hız, kritik',
  golge: 'Kaçınma, zehir, sürekli hasar',
  firtina: 'Elemental hasar, büyü',
  ruhlar: 'Enerji kalkanı, minyon, aura',
};

// Branch angles (degrees, 0 = right, clockwise)
const BRANCH_ANGLES: Record<BranchId, number> = {
  demir: 0,
  kale: 60,
  avci: 120,
  golge: 180,
  firtina: 240,
  ruhlar: 300,
};

// ========================
// PassiveNode
// ========================

export interface PassiveNode {
  id: string;
  name: string;
  description: string;
  type: PassiveNodeType;
  category: NodeCategory;
  bonuses: PassiveBonuses;
  position: { x: number; y: number };
  connections: string[];
  branchId?: BranchId;
  isAttributeNode?: boolean;
  attributeAmount?: number;
  keystoneEffect?: string;
  modifiers?: Modifier[];
  triggerEffects?: TriggerEffect[];
  // Legacy fields for UI compat until Faz 6 rewrite
  classRequirement?: string;
  masteryData?: { theme: string; options: { id: string; name: string; description: string; modifiers: Modifier[]; triggerEffects?: TriggerEffect[]; icon?: string }[]; requiredNotables: string[] };
  jewelSocketData?: { socketType: string };
}

// ========================
// State
// ========================

export interface PassiveTreeState {
  allocatedNodes: Set<string>;
  availablePoints: number;
  branchPoints: number; // Points available to select starting branches (lv10=1, lv40=1)
  attributeChoices: Map<string, 'strength' | 'agility' | 'spirit'>;
  // Legacy fields for UI compat until Faz 6 rewrite
  masterySelections: Map<string, string>;
  insertedJewels: Map<string, unknown>;
}

// ========================
// Helpers
// ========================

function pos(radius: number, angleDeg: number): { x: number; y: number } {
  const angleRad = (angleDeg - 90) * Math.PI / 180;
  return {
    x: Math.round(radius * Math.cos(angleRad)),
    y: Math.round(radius * Math.sin(angleRad)),
  };
}

/** Create a position along a branch at a given distance, with optional angular offset */
function branchPos(branchAngle: number, radius: number, offsetDeg: number = 0): { x: number; y: number } {
  return pos(radius, branchAngle + offsetDeg);
}

// Radius tiers
const R_START = 50;   // Branch start
const R1 = 120;       // First small nodes
const R2 = 170;       // Second tier
const R3 = 220;       // Third tier
const R4 = 270;       // Fourth tier
// R5 = 320 reserved for future use
const R_NOTABLE1 = 370; // First notable
const R6 = 400;       // Sixth tier
const R7 = 430;       // Seventh tier
const R_NOTABLE2 = 460; // Second notable
const R_KEYSTONE = 500;  // Keystone


// ========================
// Node creation helpers
// ========================

interface SmallNodeDef {
  id: string;
  name: string;
  description: string;
  category: NodeCategory;
  bonuses: PassiveBonuses;
}

interface NotableNodeDef {
  id: string;
  name: string;
  description: string;
  category: NodeCategory;
  bonuses: PassiveBonuses;
  modifiers?: Modifier[];
  triggerEffects?: TriggerEffect[];
}

interface KeystoneNodeDef {
  id: string;
  name: string;
  description: string;
  category: NodeCategory;
  bonuses: PassiveBonuses;
  keystoneEffect: string;
  modifiers?: Modifier[];
  triggerEffects?: TriggerEffect[];
}

// ========================
// PassiveTreeManager
// ========================

export class PassiveTreeManager {
  private nodes: Map<string, PassiveNode> = new Map();
  private state: PassiveTreeState;
  private selectedBranches: BranchId[] = [];

  constructor() {
    this.state = {
      allocatedNodes: new Set(),
      availablePoints: 0,
      branchPoints: 0,
      attributeChoices: new Map(),
      masterySelections: new Map(),
      insertedJewels: new Map(),
    };
    this.initializeTree();
  }

  // ========================
  // Branch selection
  // ========================

  getSelectedBranches(): BranchId[] {
    return [...this.selectedBranches];
  }

  canSelectBranch(branchId: BranchId): boolean {
    if (this.selectedBranches.includes(branchId)) return false;
    return this.selectedBranches.length < 2;
  }

  selectBranch(branchId: BranchId): boolean {
    if (!this.canSelectBranch(branchId)) return false;
    this.selectedBranches.push(branchId);
    return true;
  }

  /** Check if a branch is accessible (selected or center) */
  isBranchAccessible(branchId: BranchId): boolean {
    return this.selectedBranches.includes(branchId);
  }

  // ========================
  // Tree initialization
  // ========================

  private initializeTree(): void {
    // Center node - everyone starts here
    this.addNode({
      id: 'center',
      name: 'Tengri\'nin Gözü',
      description: 'Yolculuğun başlangıcı',
      type: 'center',
      category: 'holy',
      bonuses: {},
      position: { x: 0, y: 0 },
      connections: ['start_demir', 'start_kale', 'start_avci', 'start_golge', 'start_firtina', 'start_ruhlar'],
    });

    this.initBranchDemir();
    this.initBranchKale();
    this.initBranchAvci();
    this.initBranchGolge();
    this.initBranchFirtina();
    this.initBranchRuhlar();
    this.initSharedKeystones();
  }

  /**
   * Load tree from JSON data (from editor export).
   * Replaces all existing nodes with the imported ones.
   */
  loadFromJSON(jsonNodes: PassiveNode[]): void {
    this.nodes.clear();
    for (const node of jsonNodes) {
      this.nodes.set(node.id, { ...node });
    }
  }

  /**
   * Add/replace a single node from JSON (merges with existing tree).
   * Useful for loading editor-designed nodes on top of hardcoded tree.
   */
  addNodeFromJSON(nodeData: Partial<PassiveNode> & { id: string }): void {
    const existing = this.nodes.get(nodeData.id);
    if (existing) {
      // Update existing
      Object.assign(existing, nodeData);
    } else {
      // Add new
      this.nodes.set(nodeData.id, {
        id: nodeData.id,
        name: nodeData.name || nodeData.id,
        description: nodeData.description || '',
        type: (nodeData.type as PassiveNodeType) || 'small',
        category: nodeData.category || 'strength',
        bonuses: nodeData.bonuses || {},
        position: nodeData.position || { x: 0, y: 0 },
        connections: nodeData.connections || [],
        branchId: nodeData.branchId,
        isAttributeNode: nodeData.isAttributeNode || false,
        attributeAmount: nodeData.attributeAmount,
        keystoneEffect: nodeData.keystoneEffect || '',
      });
    }

    // Also update connections on the other side
    const node = this.nodes.get(nodeData.id)!;
    for (const connId of node.connections) {
      const other = this.nodes.get(connId);
      if (other && !other.connections.includes(node.id)) {
        other.connections.push(node.id);
      }
    }
  }

  /**
   * Load tree from a JSON file URL (e.g., '/data/passive_tree.json').
   * Returns a promise. Falls back to hardcoded tree on failure.
   */
  async loadFromFile(url: string): Promise<boolean> {
    try {
      const resp = await fetch(url);
      if (!resp.ok) return false;
      const data = await resp.json() as PassiveNode[];
      if (!Array.isArray(data) || data.length === 0) return false;
      this.loadFromJSON(data);
      return true;
    } catch {
      return false;
    }
  }

  /** Export all nodes as JSON-serializable array */
  exportToJSON(): PassiveNode[] {
    return Array.from(this.nodes.values());
  }

  // ========================
  // DEMIR (Iron) - Melee, Physical - 0°
  // ========================
  private initBranchDemir(): void {
    const angle = BRANCH_ANGLES.demir;
    const B: BranchId = 'demir';

    // Branch start
    this.addNode({
      id: 'start_demir', name: 'Demir Yolu', description: 'Yakın dövüş ve fiziksel güç',
      type: 'branch_start', category: 'physical', bonuses: {},
      position: branchPos(angle, R_START), connections: ['center', 'demir_a1', 'demir_a2'],
      branchId: B,
    });

    // Path A: Physical damage + Attribute nodes
    this.addAttrNode('demir_a1', B, branchPos(angle, R1, -8), ['start_demir', 'demir_a3']);
    this.addSmallNode('demir_a3', B, branchPos(angle, R2, -8), ['demir_a1', 'demir_a5'],
      { id: 'demir_a3', name: 'Fiziksel Güç', description: '+%6 Fiziksel Hasar', category: 'physical', bonuses: { percentPhysicalDamage: 6 } });
    this.addAttrNode('demir_a5', B, branchPos(angle, R3, -8), ['demir_a3', 'demir_a7']);
    this.addSmallNode('demir_a7', B, branchPos(angle, R4, -8), ['demir_a5', 'demir_notable1'],
      { id: 'demir_a7', name: 'Darbe Gücü', description: '+%8 Fiziksel Hasar', category: 'physical', bonuses: { percentPhysicalDamage: 8 } });

    // Path B: Attack speed + Attribute nodes
    this.addAttrNode('demir_a2', B, branchPos(angle, R1, 8), ['start_demir', 'demir_a4']);
    this.addSmallNode('demir_a4', B, branchPos(angle, R2, 8), ['demir_a2', 'demir_a6'],
      { id: 'demir_a4', name: 'Hız', description: '+%5 Saldırı Hızı', category: 'attack_speed', bonuses: { percentAttackSpeed: 5 } });
    this.addAttrNode('demir_a6', B, branchPos(angle, R3, 8), ['demir_a4', 'demir_a8']);
    this.addSmallNode('demir_a8', B, branchPos(angle, R4, 8), ['demir_a6', 'demir_notable1'],
      { id: 'demir_a8', name: 'Can Gücü', description: '+%6 Can', category: 'life', bonuses: { percentHp: 6 } });

    // Notable 1: "Öldürünce 3s +%25 hasar"
    this.addNotableNode('demir_notable1', B, branchPos(angle, R_NOTABLE1), ['demir_a7', 'demir_a8', 'demir_b1', 'demir_b2'],
      {
        id: 'demir_notable1', name: 'Kan Çılgınlığı',
        description: 'Öldürünce 3s boyunca +%25 Hasar',
        category: 'physical', bonuses: {},
        triggerEffects: [{
          id: 'demir_killbuff', name: 'Kan Çılgınlığı', trigger: 'on_kill',
          condition: { chance: 100 },
          effect: { type: 'apply_modifier', modifier: { stat: 'damage', type: 'increased', value: 25, source: 'passive_demir_notable1' }, duration: 3 },
        }],
      });

    // Post-notable paths
    this.addAttrNode('demir_b1', B, branchPos(angle, R6, -6), ['demir_notable1', 'demir_b3']);
    this.addAttrNode('demir_b2', B, branchPos(angle, R6, 6), ['demir_notable1', 'demir_b4']);
    this.addSmallNode('demir_b3', B, branchPos(angle, R7, -6), ['demir_b1', 'demir_notable2'],
      { id: 'demir_b3', name: 'Yıkıcı Darbe', description: '+%10 Fiziksel Hasar', category: 'physical', bonuses: { percentPhysicalDamage: 10 } });
    this.addSmallNode('demir_b4', B, branchPos(angle, R7, 6), ['demir_b2', 'demir_notable2'],
      { id: 'demir_b4', name: 'Acımasız', description: '+%8 Kritik Hasarı', category: 'crit', bonuses: { percentCritDamage: 8 } });

    // Notable 2: "Can %50 altında saldırı hızı 2x"
    this.addNotableNode('demir_notable2', B, branchPos(angle, R_NOTABLE2), ['demir_b3', 'demir_b4', 'demir_key1'],
      {
        id: 'demir_notable2', name: 'Çaresiz Öfke',
        description: 'Can %50 altındayken Saldırı Hızı 2 katına çıkar',
        category: 'attack_speed', bonuses: {},
        triggerEffects: [{
          id: 'demir_lowhp_atkspd', name: 'Çaresiz Öfke', trigger: 'on_low_hp',
          condition: { hpThreshold: 50 },
          effect: { type: 'apply_modifier', modifier: { stat: 'attackSpeed', type: 'more', value: 100, source: 'passive_demir_notable2' } },
        }],
      });

    // Keystone: Demir Yumruk
    this.addKeystoneNode('demir_key1', B, branchPos(angle, R_KEYSTONE), ['demir_notable2'],
      {
        id: 'demir_key1', name: 'Demir Yumruk',
        description: 'Yakın dövüş hasarı +%60 more\nAlınan hasar +%25 more',
        category: 'physical', bonuses: {},
        keystoneEffect: 'melee_more_60_taken_more_25',
        modifiers: [
          { stat: 'damage', type: 'more', value: 60, source: 'keystone_demir_yumruk' },
        ],
      });
  }

  // ========================
  // KALE (Fortress) - Tank, Armor, HP - 60°
  // ========================
  private initBranchKale(): void {
    const angle = BRANCH_ANGLES.kale;
    const B: BranchId = 'kale';

    this.addNode({
      id: 'start_kale', name: 'Kale Yolu', description: 'Dayanıklılık ve savunma',
      type: 'branch_start', category: 'defense', bonuses: {},
      position: branchPos(angle, R_START), connections: ['center', 'kale_a1', 'kale_a2'],
      branchId: B,
    });

    // Path A: Armor
    this.addAttrNode('kale_a1', B, branchPos(angle, R1, -8), ['start_kale', 'kale_a3']);
    this.addSmallNode('kale_a3', B, branchPos(angle, R2, -8), ['kale_a1', 'kale_a5'],
      { id: 'kale_a3', name: 'Sertlik', description: '+%8 Zırh', category: 'defense', bonuses: { percentArmor: 8 } });
    this.addAttrNode('kale_a5', B, branchPos(angle, R3, -8), ['kale_a3', 'kale_a7']);
    this.addSmallNode('kale_a7', B, branchPos(angle, R4, -8), ['kale_a5', 'kale_notable1'],
      { id: 'kale_a7', name: 'Çelik Duvar', description: '+%10 Zırh', category: 'defense', bonuses: { percentArmor: 10 } });

    // Path B: HP
    this.addAttrNode('kale_a2', B, branchPos(angle, R1, 8), ['start_kale', 'kale_a4']);
    this.addSmallNode('kale_a4', B, branchPos(angle, R2, 8), ['kale_a2', 'kale_a6'],
      { id: 'kale_a4', name: 'Canlılık', description: '+%6 Can', category: 'life', bonuses: { percentHp: 6 } });
    this.addAttrNode('kale_a6', B, branchPos(angle, R3, 8), ['kale_a4', 'kale_a8']);
    this.addSmallNode('kale_a8', B, branchPos(angle, R4, 8), ['kale_a6', 'kale_notable1'],
      { id: 'kale_a8', name: 'Dayanıklılık', description: '+%8 Can', category: 'life', bonuses: { percentHp: 8 } });

    // Notable 1: "Her 5s'de 1 vuruşu absorbe et"
    this.addNotableNode('kale_notable1', B, branchPos(angle, R_NOTABLE1), ['kale_a7', 'kale_a8', 'kale_b1', 'kale_b2'],
      {
        id: 'kale_notable1', name: 'Koruyucu Kalkan',
        description: 'Her 5 saniyede bir vuruşu tamamen absorbe eder',
        category: 'defense', bonuses: {},
        triggerEffects: [{
          id: 'kale_absorb', name: 'Koruyucu Kalkan', trigger: 'on_take_damage',
          condition: { cooldown: 5, chance: 100 },
          effect: { type: 'apply_modifier', modifier: { stat: 'armor', type: 'more', value: 9999, source: 'passive_kale_absorb' }, duration: 0.1 },
        }],
      });

    // Post-notable
    this.addSmallNode('kale_b1', B, branchPos(angle, R6, -6), ['kale_notable1', 'kale_b3'],
      { id: 'kale_b1', name: 'Kaya Sertliği', description: '+%10 Zırh', category: 'defense', bonuses: { percentArmor: 10 } });
    this.addSmallNode('kale_b2', B, branchPos(angle, R6, 6), ['kale_notable1', 'kale_b4'],
      { id: 'kale_b2', name: 'Yaşam Gücü', description: '+%8 Can', category: 'life', bonuses: { percentHp: 8 } });
    this.addAttrNode('kale_b3', B, branchPos(angle, R7, -6), ['kale_b1', 'kale_notable2']);
    this.addAttrNode('kale_b4', B, branchPos(angle, R7, 6), ['kale_b2', 'kale_notable2']);

    // Notable 2: "Block yapınca %30 HP kazan"
    this.addNotableNode('kale_notable2', B, branchPos(angle, R_NOTABLE2), ['kale_b3', 'kale_b4', 'kale_key1'],
      {
        id: 'kale_notable2', name: 'Karşı Atak',
        description: 'Block yapınca Can\'ın %30\'unu yenile',
        category: 'life', bonuses: {},
        triggerEffects: [{
          id: 'kale_block_heal', name: 'Karşı Atak', trigger: 'on_block',
          condition: { chance: 100 },
          effect: { type: 'heal', amount: 30, isPercent: true },
        }],
      });

    // Keystone: Ölümsüz Öfke
    this.addKeystoneNode('kale_key1', B, branchPos(angle, R_KEYSTONE), ['kale_notable2'],
      {
        id: 'kale_key1', name: 'Ölümsüz Öfke',
        description: 'Alınan hasarın %30\'u 4s\'ye yayılır\nAnlık HP regen yok',
        category: 'defense', bonuses: {},
        keystoneEffect: 'damage_taken_over_time_30_no_instant_regen',
      });
  }

  // ========================
  // AVCI (Hunter) - Projectile, Speed, Crit - 120°
  // ========================
  private initBranchAvci(): void {
    const angle = BRANCH_ANGLES.avci;
    const B: BranchId = 'avci';

    this.addNode({
      id: 'start_avci', name: 'Avcı Yolu', description: 'Menzilli saldırı ve kritik vuruş',
      type: 'branch_start', category: 'crit', bonuses: {},
      position: branchPos(angle, R_START), connections: ['center', 'avci_a1', 'avci_a2'],
      branchId: B,
    });

    // Path A: Crit
    this.addAttrNode('avci_a1', B, branchPos(angle, R1, -8), ['start_avci', 'avci_a3']);
    this.addSmallNode('avci_a3', B, branchPos(angle, R2, -8), ['avci_a1', 'avci_a5'],
      { id: 'avci_a3', name: 'Keskin Göz', description: '+%8 Kritik Şansı', category: 'crit', bonuses: { percentCritChance: 8 } });
    this.addAttrNode('avci_a5', B, branchPos(angle, R3, -8), ['avci_a3', 'avci_a7']);
    this.addSmallNode('avci_a7', B, branchPos(angle, R4, -8), ['avci_a5', 'avci_notable1'],
      { id: 'avci_a7', name: 'Ölümcül Vuruş', description: '+%10 Kritik Hasarı', category: 'crit', bonuses: { percentCritDamage: 10 } });

    // Path B: Speed + projectile
    this.addAttrNode('avci_a2', B, branchPos(angle, R1, 8), ['start_avci', 'avci_a4']);
    this.addSmallNode('avci_a4', B, branchPos(angle, R2, 8), ['avci_a2', 'avci_a6'],
      { id: 'avci_a4', name: 'Çeviklik', description: '+%5 Saldırı Hızı', category: 'attack_speed', bonuses: { percentAttackSpeed: 5 } });
    this.addAttrNode('avci_a6', B, branchPos(angle, R3, 8), ['avci_a4', 'avci_a8']);
    this.addSmallNode('avci_a8', B, branchPos(angle, R4, 8), ['avci_a6', 'avci_notable1'],
      { id: 'avci_a8', name: 'Hareket', description: '+%4 Hareket Hızı', category: 'speed', bonuses: { percentMovementSpeed: 4 } });

    // Notable 1: "Vuruşlar %20 şansla 2 kere vurur"
    this.addNotableNode('avci_notable1', B, branchPos(angle, R_NOTABLE1), ['avci_a7', 'avci_a8', 'avci_b1', 'avci_b2'],
      {
        id: 'avci_notable1', name: 'İkili Atış',
        description: 'Vuruşlar %20 şansla 2 kere vurur',
        category: 'crit', bonuses: {},
        triggerEffects: [{
          id: 'avci_doublehit', name: 'İkili Atış', trigger: 'on_hit',
          condition: { chance: 20 },
          effect: { type: 'deal_damage', damage: 0, element: 'physical' },
        }],
      });

    // Post-notable
    this.addSmallNode('avci_b1', B, branchPos(angle, R6, -6), ['avci_notable1', 'avci_b3'],
      { id: 'avci_b1', name: 'Hassasiyet', description: '+%10 Kritik Şansı', category: 'crit', bonuses: { percentCritChance: 10 } });
    this.addSmallNode('avci_b2', B, branchPos(angle, R6, 6), ['avci_notable1', 'avci_b4'],
      { id: 'avci_b2', name: 'Hız Ustası', description: '+%6 Saldırı Hızı', category: 'attack_speed', bonuses: { percentAttackSpeed: 6 } });
    this.addAttrNode('avci_b3', B, branchPos(angle, R7, -6), ['avci_b1', 'avci_notable2']);
    this.addAttrNode('avci_b4', B, branchPos(angle, R7, 6), ['avci_b2', 'avci_notable2']);

    // Notable 2: +1 Projectile
    this.addNotableNode('avci_notable2', B, branchPos(angle, R_NOTABLE2), ['avci_b3', 'avci_b4', 'avci_key1'],
      {
        id: 'avci_notable2', name: 'Ok Yağmuru',
        description: '+1 Mermi Sayısı',
        category: 'crit', bonuses: { projectileCount: 1 },
      });

    // Keystone: Keskin Nişancı
    this.addKeystoneNode('avci_key1', B, branchPos(angle, R_KEYSTONE), ['avci_notable2'],
      {
        id: 'avci_key1', name: 'Keskin Nişancı',
        description: 'Mermi hasarı +%50 more, +2 delme\nYakın mesafe hasarı %40 less',
        category: 'crit', bonuses: { projectileCount: 0 },
        keystoneEffect: 'projectile_more_50_pierce_2_close_less_40',
        modifiers: [
          { stat: 'damage', type: 'more', value: 50, source: 'keystone_keskin_nisanci' },
        ],
      });
  }

  // ========================
  // GOLGE (Shadow) - Evasion, Poison, DoT - 180°
  // ========================
  private initBranchGolge(): void {
    const angle = BRANCH_ANGLES.golge;
    const B: BranchId = 'golge';

    this.addNode({
      id: 'start_golge', name: 'Gölge Yolu', description: 'Kaçınma, zehir ve sürekli hasar',
      type: 'branch_start', category: 'evasion', bonuses: {},
      position: branchPos(angle, R_START), connections: ['center', 'golge_a1', 'golge_a2'],
      branchId: B,
    });

    // Path A: Evasion + dodge
    this.addAttrNode('golge_a1', B, branchPos(angle, R1, -8), ['start_golge', 'golge_a3']);
    this.addSmallNode('golge_a3', B, branchPos(angle, R2, -8), ['golge_a1', 'golge_a5'],
      { id: 'golge_a3', name: 'Gölge Adım', description: '+%8 Kaçınma', category: 'evasion', bonuses: { percentEvasion: 8 } });
    this.addAttrNode('golge_a5', B, branchPos(angle, R3, -8), ['golge_a3', 'golge_a7']);
    this.addSmallNode('golge_a7', B, branchPos(angle, R4, -8), ['golge_a5', 'golge_notable1'],
      { id: 'golge_a7', name: 'Rüzgar Gibi', description: '+%4 Hareket Hızı', category: 'speed', bonuses: { percentMovementSpeed: 4 } });

    // Path B: DoT + poison
    this.addAttrNode('golge_a2', B, branchPos(angle, R1, 8), ['start_golge', 'golge_a4']);
    this.addSmallNode('golge_a4', B, branchPos(angle, R2, 8), ['golge_a2', 'golge_a6'],
      { id: 'golge_a4', name: 'Zehirli Bıçak', description: '+%8 Sürekli Hasar', category: 'dot', bonuses: { dotDamage: 8 } });
    this.addAttrNode('golge_a6', B, branchPos(angle, R3, 8), ['golge_a4', 'golge_a8']);
    this.addSmallNode('golge_a8', B, branchPos(angle, R4, 8), ['golge_a6', 'golge_notable1'],
      { id: 'golge_a8', name: 'Yavaş Ölüm', description: '+%10 Sürekli Hasar', category: 'dot', bonuses: { dotDamage: 10 } });

    // Notable 1: "Poison'lı düşmanlardan stack başı %1 HP leech"
    this.addNotableNode('golge_notable1', B, branchPos(angle, R_NOTABLE1), ['golge_a7', 'golge_a8', 'golge_b1', 'golge_b2'],
      {
        id: 'golge_notable1', name: 'Zehir Emici',
        description: 'Zehirli düşmanlardan stack başı %1 Can çalma',
        category: 'leech', bonuses: { lifeLeechPercent: 1 },
        triggerEffects: [{
          id: 'golge_poison_leech', name: 'Zehir Emici', trigger: 'on_hit',
          condition: { chance: 100 },
          effect: { type: 'leech', stat: 'hp', percent: 1 },
        }],
      });

    // Post-notable
    this.addSmallNode('golge_b1', B, branchPos(angle, R6, -6), ['golge_notable1', 'golge_b3'],
      { id: 'golge_b1', name: 'Kaçış Ustası', description: '+%10 Kaçınma', category: 'evasion', bonuses: { percentEvasion: 10 } });
    this.addSmallNode('golge_b2', B, branchPos(angle, R6, 6), ['golge_notable1', 'golge_b4'],
      { id: 'golge_b2', name: 'Derin Zehir', description: '+%10 Sürekli Hasar', category: 'dot', bonuses: { dotDamage: 10 } });
    this.addAttrNode('golge_b3', B, branchPos(angle, R7, -6), ['golge_b1', 'golge_notable2']);
    this.addAttrNode('golge_b4', B, branchPos(angle, R7, 6), ['golge_b2', 'golge_key2']);

    // Notable 2: "Bleed'li düşman ölünce patlama"
    this.addNotableNode('golge_notable2', B, branchPos(angle, R_NOTABLE2, -6), ['golge_b3', 'golge_key1'],
      {
        id: 'golge_notable2', name: 'Kanayan Patlama',
        description: 'Kanayan düşman öldüğünde patlayarak çevreye hasar verir',
        category: 'dot', bonuses: {},
        triggerEffects: [{
          id: 'golge_bleed_explode', name: 'Kanayan Patlama', trigger: 'on_kill',
          condition: { chance: 100 },
          effect: { type: 'deal_damage', damage: 0, element: 'physical', aoe: 100 },
        }],
      });

    // Keystone 1: Gölge Dansı
    this.addKeystoneNode('golge_key1', B, branchPos(angle, R_KEYSTONE, -8), ['golge_notable2'],
      {
        id: 'golge_key1', name: 'Gölge Dansı',
        description: 'Kaçınma +%100 more\nİsabet eden vuruşlar +%30 more hasar verir',
        category: 'evasion', bonuses: {},
        keystoneEffect: 'evasion_more_100_hit_taken_more_30',
        modifiers: [
          { stat: 'evasion', type: 'more', value: 100, source: 'keystone_golge_dansi' },
        ],
      });

    // Keystone 2: Zehir Ustası
    this.addKeystoneNode('golge_key2', B, branchPos(angle, R_KEYSTONE, 8), ['golge_b4'],
      {
        id: 'golge_key2', name: 'Zehir Ustası',
        description: 'Tüm hasarın %30\'u poison olarak da uygulanır\nDirekt vuruş hasarı %20 less',
        category: 'dot', bonuses: {},
        keystoneEffect: 'all_damage_30_as_poison_direct_less_20',
        modifiers: [
          { stat: 'damage', type: 'more', value: -20, source: 'keystone_zehir_ustasi' },
        ],
      });
  }

  // ========================
  // FIRTINA (Storm) - Elemental, Spells - 240°
  // ========================
  private initBranchFirtina(): void {
    const angle = BRANCH_ANGLES.firtina;
    const B: BranchId = 'firtina';

    this.addNode({
      id: 'start_firtina', name: 'Fırtına Yolu', description: 'Elemental hasar ve büyü gücü',
      type: 'branch_start', category: 'lightning', bonuses: {},
      position: branchPos(angle, R_START), connections: ['center', 'firtina_a1', 'firtina_a2'],
      branchId: B,
    });

    // Path A: Elemental damage
    this.addAttrNode('firtina_a1', B, branchPos(angle, R1, -8), ['start_firtina', 'firtina_a3']);
    this.addSmallNode('firtina_a3', B, branchPos(angle, R2, -8), ['firtina_a1', 'firtina_a5'],
      { id: 'firtina_a3', name: 'Elemental Güç', description: '+%6 Elemental Hasar', category: 'lightning', bonuses: { percentElementalDamage: 6 } });
    this.addAttrNode('firtina_a5', B, branchPos(angle, R3, -8), ['firtina_a3', 'firtina_a7']);
    this.addSmallNode('firtina_a7', B, branchPos(angle, R4, -8), ['firtina_a5', 'firtina_notable1'],
      { id: 'firtina_a7', name: 'Büyü Gücü', description: '+%6 Büyü Hasarı', category: 'spell', bonuses: { percentSpellDamage: 6 } });

    // Path B: Cast speed + mana
    this.addAttrNode('firtina_a2', B, branchPos(angle, R1, 8), ['start_firtina', 'firtina_a4']);
    this.addSmallNode('firtina_a4', B, branchPos(angle, R2, 8), ['firtina_a2', 'firtina_a6'],
      { id: 'firtina_a4', name: 'Hızlı Büyü', description: '+%5 Büyü Hızı', category: 'spell', bonuses: { percentCastSpeed: 5 } });
    this.addAttrNode('firtina_a6', B, branchPos(angle, R3, 8), ['firtina_a4', 'firtina_a8']);
    this.addSmallNode('firtina_a8', B, branchPos(angle, R4, 8), ['firtina_a6', 'firtina_notable1'],
      { id: 'firtina_a8', name: 'Mana Havuzu', description: '+%8 Mana', category: 'mana', bonuses: { percentMana: 8 } });

    // Notable 1: +%8 AOE, "Mana harcayınca %10'u ES olur"
    this.addNotableNode('firtina_notable1', B, branchPos(angle, R_NOTABLE1), ['firtina_a7', 'firtina_a8', 'firtina_b1', 'firtina_b2'],
      {
        id: 'firtina_notable1', name: 'Fırtına Gözü',
        description: '+%8 Alan Etkisi\nMana harcayınca %10\'u Enerji Kalkanı olur',
        category: 'lightning', bonuses: { areaOfEffect: 8 },
        triggerEffects: [{
          id: 'firtina_mana_to_es', name: 'Fırtına Gözü', trigger: 'on_skill_use',
          condition: { chance: 100 },
          effect: { type: 'apply_modifier', modifier: { stat: 'energyShield', type: 'flat', value: 10, source: 'passive_firtina_notable1' }, duration: 4 },
        }],
      });

    // Post-notable
    this.addSmallNode('firtina_b1', B, branchPos(angle, R6, -6), ['firtina_notable1', 'firtina_b3'],
      { id: 'firtina_b1', name: 'Elementlerin Öfkesi', description: '+%10 Elemental Hasar', category: 'lightning', bonuses: { percentElementalDamage: 10 } });
    this.addSmallNode('firtina_b2', B, branchPos(angle, R6, 6), ['firtina_notable1', 'firtina_b4'],
      { id: 'firtina_b2', name: 'Büyü Ustası', description: '+%8 Büyü Hasarı', category: 'spell', bonuses: { percentSpellDamage: 8 } });
    this.addAttrNode('firtina_b3', B, branchPos(angle, R7, -6), ['firtina_b1', 'firtina_notable2']);
    this.addAttrNode('firtina_b4', B, branchPos(angle, R7, 6), ['firtina_b2', 'firtina_notable2']);

    // Notable 2: Bigger AOE + elemental
    this.addNotableNode('firtina_notable2', B, branchPos(angle, R_NOTABLE2), ['firtina_b3', 'firtina_b4', 'firtina_key1'],
      {
        id: 'firtina_notable2', name: 'Yıldırım Çağrısı',
        description: '+%10 Alan Etkisi\n+%10 Elemental Hasar',
        category: 'lightning', bonuses: { areaOfEffect: 10, percentElementalDamage: 10 },
      });

    // Keystone: Elemental Usta
    this.addKeystoneNode('firtina_key1', B, branchPos(angle, R_KEYSTONE), ['firtina_notable2'],
      {
        id: 'firtina_key1', name: 'Elemental Usta',
        description: 'Elemental hasar +%40 more\nMana maliyeti +%50 more',
        category: 'lightning', bonuses: {},
        keystoneEffect: 'elemental_more_40_mana_cost_more_50',
        modifiers: [
          { stat: 'spellDamage', type: 'more', value: 40, source: 'keystone_elemental_usta' },
        ],
      });
  }

  // ========================
  // RUHLAR (Spirits) - ES, Minion, Aura - 300°
  // ========================
  private initBranchRuhlar(): void {
    const angle = BRANCH_ANGLES.ruhlar;
    const B: BranchId = 'ruhlar';

    this.addNode({
      id: 'start_ruhlar', name: 'Ruhlar Yolu', description: 'Enerji kalkanı, minyon ve aura',
      type: 'branch_start', category: 'spirit', bonuses: {},
      position: branchPos(angle, R_START), connections: ['center', 'ruhlar_a1', 'ruhlar_a2'],
      branchId: B,
    });

    // Path A: Energy Shield
    this.addAttrNode('ruhlar_a1', B, branchPos(angle, R1, -8), ['start_ruhlar', 'ruhlar_a3']);
    this.addSmallNode('ruhlar_a3', B, branchPos(angle, R2, -8), ['ruhlar_a1', 'ruhlar_a5'],
      { id: 'ruhlar_a3', name: 'Ruh Kalkanı', description: '+%8 Enerji Kalkanı', category: 'energy_shield', bonuses: { percentEnergyShield: 8 } });
    this.addAttrNode('ruhlar_a5', B, branchPos(angle, R3, -8), ['ruhlar_a3', 'ruhlar_a7']);
    this.addSmallNode('ruhlar_a7', B, branchPos(angle, R4, -8), ['ruhlar_a5', 'ruhlar_notable1'],
      { id: 'ruhlar_a7', name: 'Mana Akışı', description: '+%8 Mana Yenileme', category: 'mana', bonuses: { percentManaRegen: 8 } });

    // Path B: Minion
    this.addAttrNode('ruhlar_a2', B, branchPos(angle, R1, 8), ['start_ruhlar', 'ruhlar_a4']);
    this.addSmallNode('ruhlar_a4', B, branchPos(angle, R2, 8), ['ruhlar_a2', 'ruhlar_a6'],
      { id: 'ruhlar_a4', name: 'Ruh Bağı', description: '+%8 Minyon Hasarı', category: 'minion', bonuses: { minionDamage: 8 } });
    this.addAttrNode('ruhlar_a6', B, branchPos(angle, R3, 8), ['ruhlar_a4', 'ruhlar_a8']);
    this.addSmallNode('ruhlar_a8', B, branchPos(angle, R4, 8), ['ruhlar_a6', 'ruhlar_notable1'],
      { id: 'ruhlar_a8', name: 'Ruh Dayanıklılığı', description: '+%8 Minyon Canı', category: 'minion', bonuses: { minionLife: 8 } });

    // Notable 1: "Minion leech'i sana da uygulanır"
    this.addNotableNode('ruhlar_notable1', B, branchPos(angle, R_NOTABLE1), ['ruhlar_a7', 'ruhlar_a8', 'ruhlar_b1', 'ruhlar_b2'],
      {
        id: 'ruhlar_notable1', name: 'Ruh Bağlantısı',
        description: 'Minyon Can çalması sana da uygulanır',
        category: 'minion', bonuses: {},
        triggerEffects: [{
          id: 'ruhlar_minion_leech', name: 'Ruh Bağlantısı', trigger: 'on_hit',
          condition: { chance: 100, skillType: 'minion' },
          effect: { type: 'leech', stat: 'hp', percent: 1 },
        }],
      });

    // Post-notable
    this.addSmallNode('ruhlar_b1', B, branchPos(angle, R6, -6), ['ruhlar_notable1', 'ruhlar_b3'],
      { id: 'ruhlar_b1', name: 'Güçlü Kalkan', description: '+%10 Enerji Kalkanı', category: 'energy_shield', bonuses: { percentEnergyShield: 10 } });
    this.addSmallNode('ruhlar_b2', B, branchPos(angle, R6, 6), ['ruhlar_notable1', 'ruhlar_b4'],
      { id: 'ruhlar_b2', name: 'Ruh Ordusu', description: '+%10 Minyon Hasarı', category: 'minion', bonuses: { minionDamage: 10 } });
    this.addAttrNode('ruhlar_b3', B, branchPos(angle, R7, -6), ['ruhlar_b1', 'ruhlar_notable2']);
    this.addAttrNode('ruhlar_b4', B, branchPos(angle, R7, 6), ['ruhlar_b2', 'ruhlar_key1']);

    // Notable 2: +1 Max Totem
    this.addNotableNode('ruhlar_notable2', B, branchPos(angle, R_NOTABLE2, -6), ['ruhlar_b3', 'shared_cam_top'],
      {
        id: 'ruhlar_notable2', name: 'Totem Ustası',
        description: '+1 Maksimum Totem',
        category: 'minion', bonuses: { maxTotem: 1 },
      });

    // Keystone: Ruh Ordusu
    this.addKeystoneNode('ruhlar_key1', B, branchPos(angle, R_KEYSTONE, 6), ['ruhlar_b4'],
      {
        id: 'ruhlar_key1', name: 'Ruh Ordusu',
        description: '+3 minyon limiti\nMinyon hasarı +%30 more\nKendi hasarın %80 less',
        category: 'minion', bonuses: { minionDamage: 30 },
        keystoneEffect: 'minion_plus_3_minion_more_30_self_less_80',
        modifiers: [
          { stat: 'damage', type: 'more', value: -80, source: 'keystone_ruh_ordusu' },
        ],
      });
  }

  // ========================
  // Shared Keystones (between branches)
  // ========================
  private initSharedKeystones(): void {
    // Cam Top: between Fırtına (240°) and Ruhlar (300°) = 270°
    this.addKeystoneNode('shared_cam_top', undefined, branchPos(270, R_KEYSTONE), ['ruhlar_notable2'],
      {
        id: 'shared_cam_top', name: 'Cam Top',
        description: 'Enerji Kalkanı +%100 more\nBüyü hasarı +%30 more\nKaranlık hasar ES\'yi bypass edemez\nCan = 1, Zırh = 0, Kaçınma = 0',
        category: 'energy_shield', bonuses: {},
        keystoneEffect: 'es_more_100_spell_more_30_hp1_armor0_evasion0',
        modifiers: [
          { stat: 'energyShield', type: 'more', value: 100, source: 'keystone_cam_top' },
          { stat: 'spellDamage', type: 'more', value: 30, source: 'keystone_cam_top' },
        ],
      });

    // Berserker: between Demir (0°) and Kale (60°) = 30°
    // Accessible from demir side
    this.addSmallNode('demir_to_berserker', 'demir', branchPos(15, R7), ['demir_notable2', 'shared_berserker'],
      { id: 'demir_to_berserker', name: 'Savaş Ateşi', description: '+%6 Hasar', category: 'physical', bonuses: { percentDamage: 6 } });

    this.addKeystoneNode('shared_berserker', undefined, branchPos(30, R_KEYSTONE), ['demir_to_berserker'],
      {
        id: 'shared_berserker', name: 'Berserker',
        description: 'Vuruştukça stack (max 20)\nStack başı +%3 more hasar\nStack başı alınan hasar +%1 more',
        category: 'physical', bonuses: {},
        keystoneEffect: 'hit_stacks_max20_per_stack_3more_1more_taken',
      });
  }

  // ========================
  // Node creation helpers
  // ========================

  private addAttrNode(id: string, branchId: BranchId, position: { x: number; y: number }, connections: string[]): void {
    this.addNode({
      id, name: 'Nitelik', description: 'Güç, Çeviklik veya Ruh seçimi (+5)',
      type: 'small', category: 'strength',
      bonuses: {},
      position, connections, branchId,
      isAttributeNode: true, attributeAmount: 5,
    });
  }

  private addSmallNode(_id: string, branchId: BranchId | undefined, position: { x: number; y: number }, connections: string[], def: SmallNodeDef): void {
    this.addNode({
      id: def.id, name: def.name, description: def.description,
      type: 'small', category: def.category,
      bonuses: def.bonuses,
      position, connections, branchId: branchId as BranchId | undefined,
    });
  }

  private addNotableNode(_id: string, branchId: BranchId | undefined, position: { x: number; y: number }, connections: string[], def: NotableNodeDef): void {
    this.addNode({
      id: def.id, name: def.name, description: def.description,
      type: 'notable', category: def.category,
      bonuses: def.bonuses,
      position, connections, branchId: branchId as BranchId | undefined,
      modifiers: def.modifiers,
      triggerEffects: def.triggerEffects,
    });
  }

  private addKeystoneNode(_id: string, branchId: BranchId | undefined, position: { x: number; y: number }, connections: string[], def: KeystoneNodeDef): void {
    this.addNode({
      id: def.id, name: def.name, description: def.description,
      type: 'keystone', category: def.category,
      bonuses: def.bonuses,
      position, connections, branchId: branchId as BranchId | undefined,
      keystoneEffect: def.keystoneEffect,
      modifiers: def.modifiers,
      triggerEffects: def.triggerEffects,
    });
  }

  private addNode(node: PassiveNode): void {
    this.nodes.set(node.id, node);
    this.cachedAllNodes = null;
  }

  // ========================
  // Queries
  // ========================

  getNode(id: string): PassiveNode | undefined {
    return this.nodes.get(id);
  }

  private cachedAllNodes: PassiveNode[] | null = null;

  getAllNodes(): PassiveNode[] {
    if (!this.cachedAllNodes) {
      this.cachedAllNodes = Array.from(this.nodes.values());
    }
    return this.cachedAllNodes;
  }

  getState(): PassiveTreeState {
    return this.state;
  }

  setAvailablePoints(points: number): void {
    this.state.availablePoints = points;
  }

  addBranchPoint(): void {
    this.state.branchPoints++;
  }

  getBranchPoints(): number {
    return this.state.branchPoints;
  }

  /** Select a branch, consuming 1 branch point */
  selectBranchWithPoint(branchId: BranchId): boolean {
    if (this.state.branchPoints <= 0) return false;
    if (this.selectedBranches.includes(branchId)) return false;
    if (this.selectedBranches.length >= 2) return false;
    this.selectedBranches.push(branchId);
    this.state.branchPoints--;
    return true;
  }

  // ========================
  // Allocation
  // ========================

  canAllocate(nodeId: string): boolean {
    const node = this.nodes.get(nodeId);
    if (!node) return false;
    if (this.state.allocatedNodes.has(nodeId)) return false;
    if (this.state.availablePoints <= 0) return false;

    // Center node is always allocatable (no branch requirement)
    if (node.type === 'center') return true;

    // Branch start: needs center allocated + branch selected
    if (node.type === 'branch_start') {
      if (!this.state.allocatedNodes.has('center')) return false;
      if (node.branchId && !this.isBranchAccessible(node.branchId)) return false;
      return true;
    }

    // Regular nodes: must be in an accessible branch (or no branch = shared)
    if (node.branchId && !this.isBranchAccessible(node.branchId)) return false;

    // Must be connected to an already-allocated node
    return node.connections.some(connId => this.state.allocatedNodes.has(connId));
  }

  /**
   * Allocate a node. For attribute nodes, call setAttributeChoice() after.
   */
  allocateNode(nodeId: string): boolean {
    if (!this.canAllocate(nodeId)) return false;
    this.state.allocatedNodes.add(nodeId);
    this.state.availablePoints--;
    this.lastAllocatedNode = nodeId;
    return true;
  }

  private lastAllocatedNode: string | null = null;

  undoLastAllocation(): boolean {
    if (!this.lastAllocatedNode) return false;
    if (this.deallocateNode(this.lastAllocatedNode)) {
      this.lastAllocatedNode = null;
      return true;
    }
    return false;
  }

  getLastAllocatedNode(): string | null {
    return this.lastAllocatedNode;
  }

  deallocateNode(nodeId: string): boolean {
    const node = this.nodes.get(nodeId);
    if (!node) return false;
    if (!this.state.allocatedNodes.has(nodeId)) return false;
    // Can't deallocate center if other nodes depend on it
    if (node.type === 'center' || node.type === 'branch_start') return false;

    // Check if removing this node would disconnect other allocated nodes
    if (!this.isConnectedWithout(nodeId)) return false;

    this.state.allocatedNodes.delete(nodeId);
    this.state.availablePoints++;
    this.state.attributeChoices.delete(nodeId);
    return true;
  }

  /** Check connectivity of allocated nodes if we remove excludeId */
  private isConnectedWithout(excludeId: string): boolean {
    const remaining = new Set(this.state.allocatedNodes);
    remaining.delete(excludeId);
    if (remaining.size === 0) return true;

    // BFS from center (or any allocated node)
    const start = remaining.has('center') ? 'center' : remaining.values().next().value;
    if (!start) return true;

    const visited = new Set<string>();
    const queue: string[] = [start];
    visited.add(start);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const currentNode = this.nodes.get(current);
      if (!currentNode) continue;
      for (const connId of currentNode.connections) {
        if (!visited.has(connId) && remaining.has(connId)) {
          visited.add(connId);
          queue.push(connId);
        }
      }
    }

    return visited.size === remaining.size;
  }

  // ========================
  // Attribute node choice
  // ========================

  setAttributeChoice(nodeId: string, attribute: 'strength' | 'agility' | 'spirit'): boolean {
    const node = this.nodes.get(nodeId);
    if (!node?.isAttributeNode) return false;
    if (!this.state.allocatedNodes.has(nodeId)) return false;
    this.state.attributeChoices.set(nodeId, attribute);
    return true;
  }

  getAttributeChoice(nodeId: string): 'strength' | 'agility' | 'spirit' | undefined {
    return this.state.attributeChoices.get(nodeId);
  }

  // ========================
  // Bonuses calculation
  // ========================

  getTotalBonuses(): PassiveBonuses {
    const total: PassiveBonuses = {};
    for (const nodeId of this.state.allocatedNodes) {
      const node = this.nodes.get(nodeId);
      if (!node) continue;

      // Handle attribute nodes
      if (node.isAttributeNode) {
        const choice = this.state.attributeChoices.get(nodeId);
        const amount = node.attributeAmount ?? 5;
        if (choice === 'strength') {
          total.flatStrength = (total.flatStrength || 0) + amount;
        } else if (choice === 'agility') {
          total.flatAgility = (total.flatAgility || 0) + amount;
        } else if (choice === 'spirit') {
          total.flatSpirit = (total.flatSpirit || 0) + amount;
        }
        // If no choice yet, no bonus (player must choose)
        continue;
      }

      // Regular bonuses
      for (const [key, value] of Object.entries(node.bonuses)) {
        if (typeof value === 'number') {
          (total as Record<string, number>)[key] = ((total as Record<string, number>)[key] || 0) + value;
        } else if (typeof value === 'boolean' && value) {
          (total as Record<string, boolean>)[key] = true;
        }
      }
    }
    return total;
  }

  // ========================
  // Modifier & Trigger collection
  // ========================

  getAllModifiers(): Modifier[] {
    const modifiers: Modifier[] = [];
    for (const nodeId of this.state.allocatedNodes) {
      const node = this.nodes.get(nodeId);
      if (!node) continue;
      if (node.modifiers) {
        modifiers.push(...node.modifiers);
      }
    }
    return modifiers;
  }

  getAllTriggerEffects(): TriggerEffect[] {
    const effects: TriggerEffect[] = [];
    for (const nodeId of this.state.allocatedNodes) {
      const node = this.nodes.get(nodeId);
      if (!node) continue;
      if (node.triggerEffects) effects.push(...node.triggerEffects);
    }
    return effects;
  }

  // ========================
  // Points management
  // ========================

  static readonly MAX_PASSIVE_POINTS = 40; // Level 10-50 = 40 points

  addPoint(): void {
    if (this.state.availablePoints + this.getAllocatedCount() < PassiveTreeManager.MAX_PASSIVE_POINTS) {
      this.state.availablePoints++;
    }
  }

  getAllocatedCount(): number {
    let count = 0;
    for (const nodeId of this.state.allocatedNodes) {
      const node = this.nodes.get(nodeId);
      if (node && node.type !== 'center' && node.type !== 'branch_start') count++;
    }
    return count;
  }

  // ========================
  // Reset
  // ========================

  resetTree(): void {
    let pointsToRefund = 0;
    for (const nodeId of this.state.allocatedNodes) {
      const node = this.nodes.get(nodeId);
      if (node && node.type !== 'center' && node.type !== 'branch_start') {
        pointsToRefund++;
      }
    }
    this.state.allocatedNodes.clear();
    this.state.availablePoints += pointsToRefund;
    this.state.attributeChoices.clear();
    this.lastAllocatedNode = null;
    // Note: selectedBranches are NOT reset (permanent choice)
  }

  // ========================
  // Allocatable nodes (for UI highlighting)
  // ========================

  getAllocatableNodes(): Set<string> {
    const result = new Set<string>();
    for (const node of this.getAllNodes()) {
      if (this.canAllocate(node.id)) {
        result.add(node.id);
      }
    }
    return result;
  }

  // ========================
  // Serialization
  // ========================

  serialize(): {
    allocated: string[];
    points: number;
    selectedBranches: string[];
    attributeChoices: Record<string, string>;
  } {
    return {
      allocated: Array.from(this.state.allocatedNodes),
      points: this.state.availablePoints,
      selectedBranches: [...this.selectedBranches],
      attributeChoices: Object.fromEntries(this.state.attributeChoices),
    };
  }

  deserialize(data: {
    allocated: string[];
    points: number;
    selectedBranches?: string[];
    attributeChoices?: Record<string, string>;
  }): void {
    // Restore branches first
    this.selectedBranches = ((data.selectedBranches ?? []) as BranchId[])
      .filter(b => Object.keys(BRANCH_ANGLES).includes(b))
      .slice(0, 2);

    // Validate node IDs
    const validNodes = new Set<string>();
    for (const nodeId of data.allocated) {
      if (this.nodes.has(nodeId)) {
        validNodes.add(nodeId);
      }
    }
    const invalidCount = data.allocated.length - validNodes.size;
    this.state.allocatedNodes = validNodes;
    this.state.availablePoints = data.points + invalidCount;

    // Restore attribute choices
    this.state.attributeChoices = new Map(
      Object.entries(data.attributeChoices ?? {})
        .filter(([nodeId, attr]) => validNodes.has(nodeId) && ['strength', 'agility', 'spirit'].includes(attr))
        .map(([k, v]) => [k, v as 'strength' | 'agility' | 'spirit'])
    );

    this.lastAllocatedNode = null;
  }

  // ========================
  // Backwards compatibility stubs
  // ========================

  /** @deprecated No-op in classless system */
  setPlayerClass(_className: string): void { /* no-op */ }

  /** @deprecated No-op in classless system */
  getPlayerClass(): null { return null; }

  /** @deprecated Use selectBranch + allocateNode(center) instead */
  allocateStartNode(_className: string): void {
    // Auto-allocate center node
    if (!this.state.allocatedNodes.has('center')) {
      this.state.allocatedNodes.add('center');
    }
  }

  /** @deprecated Mastery system removed. Returns false. */
  isMasteryUnlocked(_masteryNodeId: string): boolean { return false; }

  /** @deprecated Mastery system removed. Returns false. */
  selectMasteryOption(_masteryNodeId: string, _optionId: string): boolean { return false; }

  /** @deprecated Mastery system removed. Returns null. */
  getSelectedMastery(_masteryNodeId: string): { id: string; name: string; description: string; modifiers: Modifier[] } | null { return null; }

  /** @deprecated Jewel system removed. Returns null. */
  getInsertedJewel(_socketNodeId: string): { id: string; name: string } | null { return null; }
}
